import {
  isWorkbookErrorCell,
  WORKBOOK_MAX_COLUMNS,
  WORKBOOK_PIVOT_MAX_COLUMN_KEYS,
  type WorkbookCell,
  type WorkbookPivotGroup,
  type WorkbookPivotSpec,
  type WorkbookPivotValue,
  type WorkbookTable,
} from "@erp/shared-types";
import type { ColumnType } from "../formula";
import type { Schema } from "../planTypes";
import {
  compareValues,
  isEmptyCell,
  matchesFilter,
  PivotRunError,
  readDate,
  valueError,
} from "./cells";
import { compare, decimalText, divide, isZero, round } from "./decimal";
import {
  summarizeStats,
  valueStats,
  type Summary,
  type ValueStats,
} from "./stats";
import {
  EMPTY_LABEL,
  GRAND_TOTAL,
  pivotOutputColumns,
  pivotValueSlots,
  type PivotOutputColumn,
} from "./layout";

/** A pivot's input: its Fields with their Declared types, and its rows as the engine hands them on. */
export interface PivotInput {
  fields: Schema;
  rows: readonly (readonly WorkbookCell[])[];
}
export interface PivotResult {
  /** Cells as the engine hands a Result on: Numbers as exact decimal text. */
  table: WorkbookTable;
  columns: PivotOutputColumn[];
  /** Every Column key in the filtered input, in the Column fields' order, pinned or not. */
  columnKeys: string[][];
}

/** A group's label: what it shows, its identity, and how it sorts. */
export interface Label {
  id: string;
  cell: WorkbookCell;
  text: string;
  /** Values sort first, then Error cells, then Empty, in either direction. */
  rank: 0 | 1 | 2;
  sortText: string;
  sortType: ColumnType;
}

/** The Empty group: named "(Empty)" in its cell too, as its subtotal, filters and Column keys name it. */
const EMPTY: Label = {
  id: "empty",
  cell: EMPTY_LABEL,
  text: EMPTY_LABEL,
  rank: 2,
  sortText: "",
  sortType: "text",
};

function dateLabel(text: string, group: WorkbookPivotGroup): string | null {
  const date = readDate(text);
  if (!date) return null;
  const pad = (value: number) => String(value).padStart(2, "0");
  switch (group.dateGroup) {
    case "year":
      return String(date.year);
    case "quarter":
      return `${date.year}-Q${Math.ceil(date.month / 3)}`;
    case "month":
      return `${date.year}-${pad(date.month)}`;
    default:
      return `${date.year}-${pad(date.month)}-${pad(date.day)}`;
  }
}

function labelOf(
  cell: WorkbookCell,
  group: WorkbookPivotGroup,
  type: ColumnType,
): Label {
  if (isWorkbookErrorCell(cell))
    return {
      id: `error ${cell.error}`,
      cell,
      text: cell.error,
      rank: 1,
      sortText: "",
      sortType: "text",
    };
  if (isEmptyCell(cell, type)) return EMPTY;
  const text = String(cell);
  if (group.dateGroup) {
    const grouped = dateLabel(text, group);
    if (grouped === null)
      return labelOf(valueError(text, group.field, "date"), group, type);
    return {
      id: `value ${grouped}`,
      cell: grouped,
      text: grouped,
      rank: 0,
      sortText: grouped,
      sortType: "text",
    };
  }
  const shown =
    type === "boolean"
      ? text === "true"
        ? "TRUE"
        : text === "false"
          ? "FALSE"
          : text
      : text;
  return {
    id: `value ${text}`,
    cell: shown,
    text: shown,
    rank: 0,
    sortText: text,
    sortType: type,
  };
}

/**
 * A pivot's groups with their statistics: the tree its layout is read from.
 * A Row node holds, per Value, the statistics of all its rows and of its rows
 * under each Column key (by the key's label texts); a Column node, of all
 * its rows. Children are in the order their first row came, which breaks
 * every sorting tie. The pure evaluator builds it from rows; the engine
 * builds it from SQL aggregates, so both lay out the same pivot.
 */
export interface PivotNode {
  label: Label;
  /** Per Value, in `spec.values` order. */
  stats: readonly ValueStats[];
  /** Per Column key id (its label texts joined), per Value. */
  byColumn: ReadonlyMap<string, readonly ValueStats[]>;
  children: ReadonlyMap<string, PivotNode>;
}
export interface PivotTree {
  rows: PivotNode;
  columns: PivotNode;
}
export type { Label as PivotLabel };

/** A group of rows under one label, with its rows by Column key, as the rows are read. */
interface Group {
  label: Label;
  rows: number[];
  byColumn: Map<string, number[]>;
  children: Map<string, Group>;
}

function group(label: Label): Group {
  return { label, rows: [], byColumn: new Map(), children: new Map() };
}

function child(parent: Group, label: Label): Group {
  let found = parent.children.get(label.id);
  if (!found) {
    found = group(label);
    parent.children.set(label.id, found);
  }
  return found;
}

/** A Column key's id: its label texts, joined. */
export const pivotKeyId = (texts: readonly string[]): string =>
  texts.join("\u0000");

/** A group's label for a cell of a Row or Column field. */
export { labelOf as pivotLabel };
/** The label of an Empty group. */
export const EMPTY_PIVOT_LABEL: Label = EMPTY;

class PivotRows {
  private readonly fields: ReadonlyMap<
    string,
    { index: number; type: ColumnType }
  >;

  constructor(
    private readonly spec: WorkbookPivotSpec,
    private readonly input: PivotInput,
  ) {
    this.fields = new Map(
      input.fields.map((field, index) => [
        field.name,
        { index, type: field.type },
      ]),
    );
  }

  private field(name: string): { index: number; type: ColumnType } {
    const field = this.fields.get(name);
    if (!field) throw new PivotRunError(`Unknown column '${name}'.`);
    return field;
  }

  private cell(row: number, name: string): WorkbookCell {
    return this.input.rows[row]?.[this.field(name).index] ?? null;
  }

  private labels(row: number, groups: readonly WorkbookPivotGroup[]): Label[] {
    return groups.map((entry) =>
      labelOf(this.cell(row, entry.field), entry, this.field(entry.field).type),
    );
  }

  private kept(row: number): boolean {
    return this.spec.filters.every((filter) =>
      matchesFilter(
        this.cell(row, filter.column),
        filter,
        this.field(filter.column).type,
      ),
    );
  }

  private stats(rows: readonly number[]): ValueStats[] {
    return this.spec.values.map((value) =>
      valueStats(
        rows.map((row) => this.cell(row, value.field)),
        this.field(value.field).type,
      ),
    );
  }

  private node(source: Group): PivotNode {
    return {
      label: source.label,
      stats: this.stats(source.rows),
      byColumn: new Map(
        [...source.byColumn].map(([key, rows]) => [key, this.stats(rows)]),
      ),
      children: new Map(
        [...source.children].map(([id, next]) => [id, this.node(next)]),
      ),
    };
  }

  build(): PivotTree {
    const rows = group(EMPTY);
    const columns = group(EMPTY);
    const hide = this.spec.emptyGroups === "hide";
    this.input.rows.forEach((_, row) => {
      if (!this.kept(row)) return;
      const rowLabels = this.labels(row, this.spec.rows);
      const columnLabels = this.labels(row, this.spec.columns);
      if (
        hide &&
        [...rowLabels, ...columnLabels].some((label) => label.rank === 2)
      )
        return;
      const key = pivotKeyId(columnLabels.map((label) => label.text));
      let node = rows;
      const place = (target: Group) => {
        target.rows.push(row);
        const bucket = target.byColumn.get(key);
        if (bucket) bucket.push(row);
        else target.byColumn.set(key, [row]);
      };
      place(node);
      for (const label of rowLabels) {
        node = child(node, label);
        place(node);
      }
      let column = columns;
      column.rows.push(row);
      for (const label of columnLabels) {
        column = child(column, label);
        column.rows.push(row);
      }
    });
    return { rows: this.node(rows), columns: this.node(columns) };
  }
}

/** Lays a pivot's tree out as its Result: sorts groups and fills every cell. */
class PivotLayout {
  private readonly summaries = new WeakMap<
    readonly ValueStats[],
    Map<number, Summary>
  >();

  constructor(private readonly spec: WorkbookPivotSpec) {}

  /** The Value's summary of a group's statistics, memoized. */
  summary(stats: readonly ValueStats[], value: number): Summary {
    let byValue = this.summaries.get(stats);
    if (!byValue) {
      byValue = new Map();
      this.summaries.set(stats, byValue);
    }
    let found = byValue.get(value);
    if (!found) {
      const spec = this.spec.values[value];
      const of = stats[value];
      if (!spec || !of)
        throw new PivotRunError("A pivot Value does not exist.");
      found = summarizeStats(spec.summarize, of, spec.field);
      byValue.set(value, found);
    }
    return found;
  }

  /** Sorts groups by their labels or a Value's summary, as their field says. */
  sorted(groups: Iterable<PivotNode>, spec: WorkbookPivotGroup): PivotNode[] {
    const direction = spec.sortBy.direction === "desc" ? -1 : 1;
    const byLabel = (a: PivotNode, b: PivotNode, sign: number) =>
      a.label.rank - b.label.rank ||
      (a.label.rank === 0
        ? sign *
          compareValues(a.label.sortText, b.label.sortText, a.label.sortType)
        : 0);
    const { sortBy } = spec;
    if (sortBy.by === "label")
      return [...groups].sort((a, b) => byLabel(a, b, direction));
    return [...groups].sort((a, b) => {
      const x = this.summary(a.stats, sortBy.value);
      const y = this.summary(b.stats, sortBy.value);
      const rank = Number(x.kind === "error") - Number(y.kind === "error");
      if (rank) return rank;
      const order =
        x.kind === "number" && y.kind === "number"
          ? direction * compare(x.value, y.value)
          : 0;
      return order || byLabel(a, b, 1);
    });
  }

  /** The Column keys in order: each Column field's groups sorted within the one before. */
  columnKeys(root: PivotNode): string[][] {
    const keys: string[][] = [];
    const visit = (node: PivotNode, depth: number, prefix: string[]) => {
      const spec = this.spec.columns[depth];
      if (!spec) {
        keys.push(prefix);
        return;
      }
      for (const next of this.sorted(node.children.values(), spec))
        visit(next, depth + 1, [...prefix, next.label.text]);
    };
    visit(root, 0, []);
    return keys;
  }
}

/** The statistics of the total a shown-as Value is a fraction of. */
function totalStats(
  showAs: Exclude<WorkbookPivotValue["showAs"], "value">,
  {
    node,
    root,
    columnId,
  }: { node: PivotNode; root: PivotNode; columnId: string | null },
): readonly ValueStats[] | undefined {
  if (showAs === "percent-of-row") return node.stats;
  if (showAs === "percent-of-column" && columnId !== null)
    return root.byColumn.get(columnId);
  return root.stats;
}

function summaryCell(summary: Summary): WorkbookCell {
  return summary.kind === "error"
    ? summary.cell
    : decimalText(round(summary.value));
}

function fraction(part: Summary, whole: Summary): Summary {
  if (part.kind === "error") return part;
  if (whole.kind === "error") return whole;
  if (isZero(whole.value))
    return {
      kind: "error",
      cell: { error: "#DIV/0!", cause: "The total it is a share of is 0" },
    };
  return { kind: "number", value: divide(part.value, whole.value) };
}

/** The Column keys a pivot's tree holds, in the Column fields' order. */
export function pivotTreeColumnKeys(
  spec: WorkbookPivotSpec,
  tree: Pick<PivotTree, "columns">,
): string[][] {
  return spec.columns.length
    ? new PivotLayout(spec).columnKeys(tree.columns)
    : [];
}

/** Refuses a pivot spreading into more columns than a Worksheet holds. */
export function requirePivotColumns(
  spec: WorkbookPivotSpec,
  observed: readonly (readonly string[])[],
): PivotOutputColumn[] {
  const columns = pivotOutputColumns(spec, spec.columnKeys ?? observed);
  if (columns.length > WORKBOOK_MAX_COLUMNS)
    throw new PivotRunError(
      `The pivot spreads into ${columns.length} columns; a Worksheet holds at most ${WORKBOOK_MAX_COLUMNS}. Filter its Column fields or pin the columns it shows.`,
    );
  return columns;
}

/**
 * Lays a pivot's tree out as its Result, by the rule in `./layout`: each
 * Value's summary per cell, subtotal and Grand Total, shown as the value or a
 * fraction of a total.
 */
export function layOutPivot(
  spec: WorkbookPivotSpec,
  tree: PivotTree,
): PivotResult {
  if (!spec.rows.length && !spec.values.length)
    throw new PivotRunError("A pivot needs a Row field or a Value.");
  const layout = new PivotLayout(spec);
  const observed = pivotTreeColumnKeys(spec, tree);
  const shown = spec.columnKeys ?? observed;
  const columns = requirePivotColumns(spec, observed);
  const slots = pivotValueSlots(spec, shown).map((slot) => ({
    ...slot,
    id: slot.key && pivotKeyId(slot.key),
  }));
  const root = tree.rows;

  const empty = spec.emptyCells === "zero" ? "0" : null;
  const valueCells = (node: PivotNode): WorkbookCell[] =>
    slots.map(({ id, value }) => {
      const stats = id === null ? node.stats : node.byColumn.get(id);
      if (!stats) return empty;
      const cell = layout.summary(stats, value);
      const showAs = spec.values[value]?.showAs ?? "value";
      if (showAs === "value") return summaryCell(cell);
      const whole = totalStats(showAs, { node, root, columnId: id });
      if (!whole) return empty;
      return summaryCell(fraction(cell, layout.summary(whole, value)));
    });

  const depth = spec.rows.length;
  const output: WorkbookCell[][] = [];
  const blanks = (count: number): null[] =>
    Array.from({ length: count }, () => null);
  const visit = (node: PivotNode, level: number, labels: WorkbookCell[]) => {
    const groupSpec = spec.rows[level];
    if (!groupSpec) {
      output.push([...labels, ...valueCells(node)]);
      return;
    }
    for (const next of layout.sorted(node.children.values(), groupSpec)) {
      visit(next, level + 1, [...labels, next.label.cell]);
      if (groupSpec.subtotals && level < depth - 1 && spec.values.length)
        output.push([
          ...labels,
          `${next.label.text} Total`,
          ...blanks(depth - level - 1),
          ...valueCells(next),
        ]);
    }
  };
  if (depth) {
    visit(root, 0, []);
    if (spec.grandTotals.rows && spec.values.length)
      output.push([GRAND_TOTAL, ...blanks(depth - 1), ...valueCells(root)]);
  } else output.push(valueCells(root));

  return {
    table: { columns: columns.map((column) => column.name), rows: output },
    columns,
    columnKeys: observed.slice(0, WORKBOOK_PIVOT_MAX_COLUMN_KEYS),
  };
}

/**
 * Evaluates a pivot over its input's rows: Filters, then groups by Row and
 * Column fields, then lays the groups out (`layOutPivot`). The specification
 * of what a pivot gives: the engine computes the same tree in SQL for inputs
 * of any size, and differential tests hold the two to the same Result.
 */
export function evaluatePivot(
  spec: WorkbookPivotSpec,
  input: PivotInput,
): PivotResult {
  if (!spec.rows.length && !spec.values.length)
    throw new PivotRunError("A pivot needs a Row field or a Value.");
  return layOutPivot(spec, new PivotRows(spec, input).build());
}
